import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Screen } from "../components/Screen";
import { CountUp } from "../components/CountUp";
import { Ref } from "../components/Ref";
import { Reveal } from "../components/Reveal";
import { useSnak, type Detail } from "../snak/SnakContext";
import { pfsDetail, ttntDetail } from "../content/details";
const AXIS_MAX = 24;
const TICKS = [0, 6, 12, 18, 24];
export function P2Outcomes() {
  return <Screen overline={<>
          MINT · outcomes over time<Ref n={1} data-id="element-300" data-mp="jnasulzwo1jr" />
        </>} refTarget={1} footnote={<p data-id="element-301" data-mp="5pm4332pwa9f">
          Two independent, single-arm observational endpoints from the final MINT results (provisional).<Ref n={1} data-id="element-302" data-mp="9peihweslamp" /> Each
          line marks the reported median on a months axis; it is not a survival curve. Results are not comparable with each
          other, with clinical trials or with other therapies. 95% CIs and follow-up pending.
        </p>} data-id="element-299" data-mp="enfyg8gx8tty">
      <Reveal delay={0.1} data-id="element-303" data-mp="1wdrh9tom26sc">
        <h1 className="mt-5 font-display text-[32px] font-medium leading-[1.1] tracking-[-0.01em]" data-id="element-304" data-mp="1wf2l1ti23ejg">
          Beyond response: what did the study observe?
        </h1>
      </Reveal>

      <div className="mt-6 divide-y divide-[var(--s-rule)] border-y border-[var(--s-rule)]" data-id="element-305" data-mp="25f7ibatjcwjw">
        <Endpoint value={20.2} label="Median progression-free survival" detail={pfsDetail} delay={0.25} data-id="element-306" data-mp="21ckx3srde9vo" />
        <Endpoint value={14.9} label="Median time to next treatment" detail={ttntDetail} delay={0.55} data-id="element-307" data-mp="2fcc3b7bnalz4" />
      </div>
    </Screen>;
}
function Endpoint({
  value,
  label,
  detail,
  delay
}: {
  value: number;
  label: string;
  detail: Detail;
  delay: number;
}) {
  const {
    openDetail
  } = useSnak();
  const pct = value / AXIS_MAX * 100;
  const open = () => openDetail(detail);
  return <Reveal delay={delay} data-id="element-308" data-mp="2y33nl3x67wsa">
      <div role="button" tabIndex={0} onClick={open} onKeyDown={e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        open();
      }
    }} className="group block cursor-pointer py-5" data-id="element-309" data-mp="2y2lfna15bu4j">
        <div className="flex items-end justify-between gap-3" data-id="element-310" data-mp="1vp5t0zzkl21t">
          <div className="flex items-baseline gap-2.5 text-[var(--s-accent)]" data-id="element-311" data-mp="1vnl01eswlr62">
            <span className="font-display text-[78px] font-medium leading-[0.85] tracking-[-0.03em]" data-id="element-312" data-mp="1zq93t82rg7w6">
              <CountUp to={value} duration={1.8} delay={delay + 0.2} data-id="element-313" data-mp="24kdea1b8iy20" />
            </span>
            <span className="font-display text-[26px] font-medium" data-id="element-314" data-mp="29h3uqpqybvny">months</span>
          </div>
          <span className="mb-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--s-rule)] transition-colors duration-150 group-hover:border-[var(--s-fg)]" aria-hidden data-id="element-315" data-mp="2eevntn3c1ntq">
            <Plus className="h-4 w-4" strokeWidth={1.75} data-id="element-316" data-mp="p1ifznw0hhnv" />
          </span>
        </div>
        <p className="mt-3 text-[17px] leading-snug" data-id="element-317" data-mp="p3v129x2g855">
          {label}
          <Ref n={1} data-id="element-318" data-mp="t3dphfo3j1cq" />
        </p>

        <div className="mt-4" aria-hidden data-id="element-319" data-mp="ljkyy7xrkiap">
          <div className="relative h-4" data-id="element-320" data-mp="lkctrgjjebzx">
            <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--s-track)]" data-id="element-321" data-mp="llfknogv0efu" />
            <motion.div className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 bg-[var(--s-accent)]" initial={{
            width: "0%"
          }} animate={{
            width: `${pct}%`
          }} transition={{
            duration: 1.8,
            delay: delay + 0.2,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-322" data-mp="gqrr3b700tue" />
          </div>
          <div className="relative h-4" data-id="element-323" data-mp="zjcg2hjkuesx">
            {TICKS.map(t => <span key={t} className="tabular absolute -translate-x-1/2 text-[10px] text-[var(--s-muted)] first:translate-x-0 last:-translate-x-full" style={{
            left: `${t / AXIS_MAX * 100}%`
          }} data-id="element-324" data-mp="zh9gb5smh7b0">
                {t}
                {t === AXIS_MAX ? " mo" : ""}
              </span>)}
          </div>
        </div>
        <p className="overline mt-2 text-[10px] text-[var(--s-muted)]" data-id="element-325" data-mp="3b7likh9wnoer">Definition, context &amp; limitations</p>
      </div>
    </Reveal>;
}